import { useEffect, useRef } from 'react';

import type { DecisionFormData } from './useDecisionFormData';

interface UsePolicySmartInferenceProps {
  formData: DecisionFormData;
  setFormData: (updater: (prev: DecisionFormData) => DecisionFormData) => void;
}

/**
 * Custom hook to handle unidirectional smart inference for policy assessments
 * - Individual policy disagreements automatically set overall assessment to false
 * - Overall assessment is NOT automatically set to true (users must explicitly control this)
 * - Prevents unwanted cascading updates where individual changes affect all policies
 */
export function usePolicySmartInference({ formData, setFormData }: UsePolicySmartInferenceProps) {
  // Use refs to track previous values and prevent infinite loops
  const prevTriggeredStatesRef = useRef<string>('');
  const prevNonTriggeredStatesRef = useRef<string>('');

  // Smart inference: set overall policy to false when any individual policy is disagreed
  useEffect(() => {
    const triggeredStates = formData.triggeredPolicyAssessments.map(a => `${a.policyId}:${a.isCorrect}`).join(',');
    const nonTriggeredStates = formData.nonTriggeredPolicyAssessments.map(a => `${a.policyId}:${a.isCorrect}`).join(',');

    // Check if states have changed to prevent infinite loops
    if (triggeredStates === prevTriggeredStatesRef.current && nonTriggeredStates === prevNonTriggeredStatesRef.current) {
      return;
    }

    // Update refs first
    prevTriggeredStatesRef.current = triggeredStates;
    prevNonTriggeredStatesRef.current = nonTriggeredStates;

    // Check if any policy has disagreement - if so, set overall to false
    const hasDisagreement =
      formData.triggeredPolicyAssessments.some(a => a.isCorrect === false) || formData.nonTriggeredPolicyAssessments.some(a => a.isCorrect === false);

    if (hasDisagreement && formData.policiesCorrect !== false) {
      setFormData(prev => ({ ...prev, policiesCorrect: false }));
    }
  }, [formData.triggeredPolicyAssessments, formData.nonTriggeredPolicyAssessments, formData.policiesCorrect, setFormData]);

  // Note: Only "dislike" inference is implemented (individual → overall)
  // Overall assessment is NOT automatically set to "like" to prevent cascading updates.
  // Users must explicitly use the overall like/dislike buttons for positive assessments.
}
